import React, { useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { setPageTitle, setStatusBarMessage } from "../../app/actions";
import { formatBytes } from "../Common/FormatUtils"
import { compareJiraChangelogs } from "../Common/CompareFunctions"
import { MONTHS } from '../Common/ZestConstants';
import axios from "axios";
import { getJwtToken } from '../Common/Auth';
import { v4 as uuidv4 } from 'uuid';
import { getApiURL } from "../Common/envVarsReader";
import { ColumnLayout } from '@amzn/awsui-components-react/polaris';
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import Button from "@amzn/awsui-components-react/polaris/button";
import Table from "@amzn/awsui-components-react/polaris/table";
import Box from "@amzn/awsui-components-react/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import AttributeEditor from "@amzn/awsui-components-react/polaris/attribute-editor";
import Select from "@amzn/awsui-components-react/polaris/select";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Badge from "@amzn/awsui-components-react/polaris/badge";
import Tabs from "@amzn/awsui-components-react/polaris/tabs";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import ExternalLink from '../Common/ExternalLink';
import ASBMonthSelector from '../Job/ASBMonthSelector';
import { getCurEnv } from '../Common/envVarsReader';

const ValueWithLabel = ({ label, children }) => (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{children}</div>
    </div>
  );

function SearchResultsTable(props) {
    if(props.jiras) {
        let jira_url = 'issues.labcollab.net';
        if(props.environment && props.environment.value && props.environment.value === 'staging') {
            jira_url = 'issues-staging.labcollab.net';
        }

        return(
            <Table
            columnDefinitions={[
              {
                id: "id",
                header: "ID",
                cell: item => {
                    return(
                        <ExternalLink
                            base={'https://' + jira_url + '/browse/'}
                            value={item.id}
                        />
                    );
                },
                sortingField: "id"
              },
              {
                id: "cve",
                header: "CVE",
                cell: item => item.cve || "--",
                sortingField: "cve"
              },
              {
                id: "summary",
                header: "Summary",
                cell: item => {
                    if(item.summary) {
                        return (<small>{item.summary}</small>);
                    }
                    return (<small>--</small>);
                },
                sortingField: "summary"
              },
              {
                id: "updates",
                header: "Updates",
                cell: item => {
                    if(props.jiraChangelog === null) {
                        return <Spinner />;
                    }
                    if(props.jiraChangelog[item.id] && props.jiraChangelog[item.id]['changelog'] && props.jiraChangelog[item.id]['changelog'].length > 0) {
                            return (
                                <SpaceBetween size="xs">
                                    <Button
                                        onClick={(e) => {e.preventDefault(); props.setJiraChangelogModalVisible(true);}}
                                        iconAlign="left"
                                        iconName="status-warning"
                                    >
                                        {props.jiraChangelog[item.id]['changelog'].length + (props.jiraChangelog[item.id]['changelog'].length > 1 ? ' Updates' : ' Update')}
                                    </Button>
                                    <Modal
                                        onDismiss={() => props.setJiraChangelogModalVisible(false)}
                                        visible={props.jiraChangelogModalVisible}
                                        closeAriaLabel="Close"
                                        footer={
                                            <Box float="right">
                                            <SpaceBetween direction="horizontal" size="xs">
                                                <Button 
                                                    variant="link"
                                                    onClick={(e) => {e.preventDefault(); props.setJiraChangelogModalVisible(false);}}
                                                >
                                                    Close
                                                </Button>
                                            </SpaceBetween>
                                            </Box>
                                        }
                                        header={item.id + " Changelog"}
                                        size="large"
                                    >
                                        <Table
                                            columnDefinitions={[
                                                {
                                                    id: "date",
                                                    header: "Date",
                                                    cell: item => item.date || "--",
                                                    sortingField: "date"
                                                },
                                                {
                                                    id: "type",
                                                    header: "Type",
                                                    cell: item => {
                                                        switch(item.type) {
                                                            case "comment":
                                                                return <Badge color="blue">Comment</Badge>;
                                                            case "labels":
                                                                return <Badge color="green">Labels</Badge>;
                                                            case "attachment":
                                                                return <Badge color="red">Attachment</Badge>;
                                                            default:
                                                                return <Badge>{item.type}</Badge>;
                                                        }
                                                    },
                                                    sortingField: "type"
                                                },
                                                {
                                                    id: "change",
                                                    header: "Change",
                                                    cell: item => {
                                                        return (<small>{item.change || "--"}</small>);
                                                    }
                                                }
                                            ]}
                                            variant={"embedded"}
                                            wrapLines={true}
                                            items={props.jiraChangelog[item.id]['changelog'].sort(compareJiraChangelogs)}
                                            loadingText="Loading Changes"
                                            sortingDisabled
                                            empty={
                                                <Box textAlign="center" color="inherit">
                                                <b>No changes</b>
                                                <Box
                                                    padding={{ bottom: "s" }}
                                                    variant="p"
                                                    color="inherit"
                                                >
                                                    No changes for this Jira.
                                                </Box>
                                                </Box>
                                            }
                                        />
                                    </Modal>
                                </SpaceBetween>
                            );
                    }
                    return '--';
                },
                sortingField: "updates"
              },
              {
                id: "manually_closed",
                header: "Manually Closed",
                cell: item => {
                    if(props.jiraChangelog === null) {
                        return <Spinner />;
                    }
                    if(props.jiraChangelog[item.id] && props.jiraChangelog[item.id]['manually_closed']) {
                        return <Badge color="red">Closed</Badge>;
                    }
                    return '--';
                },
                sortingField: "manually_closed"
              }
            ]}
            onSelectionChange={({ detail }) =>
                props.setSelectedIssues(items => {
                    const shepherdIssues = items.filter(item => item.filing_system && item.filing_system.toLowerCase() === 'shepherd');
                    // Keep jira selections that are not currently visible (filtered out by CVE)
                    const hiddenJiraSelections = items.filter(item =>
                        (!item.filing_system || item.filing_system.toLowerCase() !== 'shepherd') &&
                        !props.jiras.some(j => j.id === item.id)
                    );
                    return [...shepherdIssues, ...hiddenJiraSelections, ...detail.selectedItems];
                })
            }
            selectedItems={props.selectedJiras.filter(item => props.jiras.some(j => j.id === item.id))}
            selectionType="multi"
            items={props.jiras}
            empty={<b>No ASB Jiras found</b>}
            loadingText="Loading Jiras"
            sortingColumn={props.sortingColumn}
            sortingDescending={props.sortingDescending}
            onSortingChange={props.onSortingChange}
            header={<Header>Jira Issues - {props.jiras.length}</Header>}
          />
        );
    }
    return '';
}

function ShepherdResultsTable(props) {
    if(props.shepherds) {
        let shepherd_url = 'shepherd.a2z.com';
        if(props.environment && props.environment.value && props.environment.value === 'staging') {
            shepherd_url = 'beta.shepherd.a2z.com';
        }

        return(
            <Table
            columnDefinitions={[
              {
                id: "id",
                header: "ID",
                cell: item => {
                    return(
                        <ExternalLink
                            base={'https://' + shepherd_url + '/issues/'}
                            value={item.id}
                        />
                    );
                },
                sortingField: "id"
              },
              {
                id: "cve",
                header: "CVE",
                cell: item => item.cve || "--",
                sortingField: "cve"
              },
              {
                id: "summary",
                header: "Summary",
                cell: item => {
                    if(item.summary) {
                        return (<small>{item.summary}</small>);
                    }
                    return (<small>--</small>);
                },
                sortingField: "summary"
              }
            ]}
            items={props.shepherds}
            onSelectionChange={({ detail }) =>
                props.setSelectedIssues(items => {
                    const nonShepherdIssues = items.filter(item => !item.filing_system || item.filing_system.toLowerCase() !== 'shepherd');
                    // Keep shepherd selections that are not currently visible (filtered out by CVE)
                    const hiddenShepherdSelections = items.filter(item =>
                        item.filing_system && item.filing_system.toLowerCase() === 'shepherd' &&
                        !props.shepherds.some(s => s.id === item.id)
                    );
                    return [...nonShepherdIssues, ...hiddenShepherdSelections, ...detail.selectedItems];
                })
            }
            selectedItems={props.selectedShepherds.filter(item => props.shepherds.some(s => s.id === item.id))}
            selectionType="multi"
            empty={<b>No Shepherd issues found</b>}
            loadingText="Loading Shepherd issues"
            sortingColumn={props.sortingColumn}
            sortingDescending={props.sortingDescending}
            onSortingChange={props.onSortingChange}
            header={<Header>Shepherd Issues - {props.shepherds.length}</Header>}
          />
        );
    }
    return '';
}

const ItemControl = React.memo(({ value, index, placeholder, setItems, prop, validate, showErrors }) => {
    const isInvalid = showErrors && validate && value && value.trim().length > 0 && !validate(value.trim());
    return (
      <FormField errorText={isInvalid ? "Invalid format. Expected: CVE-YYYY-NNNNN" : ""}>
        <Input
          value={value}
          placeholder={placeholder}
          invalid={isInvalid}
          onChange={({ detail }) => {
            setItems(items => {
              const updatedItems = [...items];
              updatedItems[index] = {
                ...updatedItems[index],
                [prop]: detail.value,
              };
              return updatedItems;
            });
          }}
        />
      </FormField>
    );
  });

function UpdateJiraForm(props) {
    if(!props.selectedIssues || props.selectedIssues.length === 0) {
        return '';
    }

    const hasShepherdSelected = props.hasShepherdSelected;

    const selectionSummary = (
        <SpaceBetween direction="horizontal" size="xs">
            <span>{props.selectedIssues.length} issue{props.selectedIssues.length > 1 ? 's' : ''} selected:</span>
            {props.selectedJiras.length > 0 && <Badge color="blue">{props.selectedJiras.length} Jira</Badge>}
            {props.selectedShepherds.length > 0 && <Badge color="green">{props.selectedShepherds.length} Shepherd</Badge>}
        </SpaceBetween>
    );

    return (
        <Container
            header={
                <Header variant="h2" description={selectionSummary}>
                    Issue Actions
                </Header>
            }
        >
            <SpaceBetween size="l">
                {/* Action selection buttons */}
                <SpaceBetween direction="horizontal" size="xs">
                    <Button
                        variant={props.activeAction === 'update' ? 'primary' : 'normal'}
                        disabled={hasShepherdSelected}
                        onClick={(e) => { e.preventDefault(); props.setActiveAction(props.activeAction === 'update' ? null : 'update'); }}
                    >
                        Update Issues
                    </Button>
                    <Button
                        variant={props.activeAction === 'close' ? 'primary' : 'normal'}
                        disabled={props.loading}
                        onClick={(e) => { e.preventDefault(); props.setActiveAction(props.activeAction === 'close' ? null : 'close'); }}
                    >
                        Close Issues
                    </Button>
                </SpaceBetween>

                {hasShepherdSelected && props.activeAction === 'update' && (
                    <Alert type="info">
                        Shepherd issues are selected. Comment, label, and attachment updates are only available for Jira issues.
                    </Alert>
                )}

                {/* Update form */}
                {props.activeAction === 'update' && (
                    <SpaceBetween size="m">
                        <FormField label="Add Comment">
                            <Textarea
                                onChange={({ detail }) => props.setUpdateComment(detail.value)}
                                value={props.updateComment}
                            />
                        </FormField>
                        <FormField>
                            <SpaceBetween size="xs">
                                <AttributeEditor
                                    items={props.jiraLabels}
                                    definition={props.labelDefinition}
                                    onAddButtonClick={props.onAddLabelButtonClick}
                                    onRemoveButtonClick={props.onRemoveLabelButtonClick}
                                    addButtonText="Add label"
                                    removeButtonText="Remove"
                                    empty="No labels configured."
                                />
                            </SpaceBetween>
                        </FormField>
                        <FormField label="Add Attachment">
                            <input
                                type="file"
                                style={{ "display": "none" }}
                                ref={props.fileInputRef}
                                onChange={e => {
                                    props.setSelectedFile(e.target.files[0]);
                                }}
                            />
                            <Button
                                onClick={(e) => { e.preventDefault(); props.selectFile(); }}
                                iconAlign="right"
                                iconName="upload"
                            >
                                Choose file
                            </Button>
                        </FormField>
                        <FileDetails selectedFile={props.selectedFile} />
                        <div align="right">
                            <UpdateButton
                                selectedJiras={props.selectedJiras}
                                loading={props.loading}
                                updateJiras={props.updateJiras}
                            />
                        </div>
                    </SpaceBetween>
                )}

                {/* Close confirmation */}
                {props.activeAction === 'close' && (
                    <SpaceBetween size="m">
                        <Alert type="warning">
                            This will close the {props.selectedIssues.length} selected issue{props.selectedIssues.length > 1 ? 's' : ''}.
                            {props.selectedShepherds.length > 0 && ' Shepherd issues will not be closed directly — Zest will generate a file listing them for manual closure by a security engineer.'}
                        </Alert>
                        <FormField label="Close Comment">
                            <Textarea
                                onChange={({ detail }) => props.setCloseComment(detail.value)}
                                value={props.closeComment}
                            />
                        </FormField>
                        <SpaceBetween size="xs">
                            {props.selectedJiras.length > 0 && <Box>Jira issues to close: {props.selectedJiras.length}</Box>}
                            {props.selectedShepherds.length > 0 && <Box>Shepherd issues (manual closure): {props.selectedShepherds.length}</Box>}
                        </SpaceBetween>
                        <div align="right">
                            <Button
                                variant="primary"
                                disabled={props.loading}
                                onClick={(e) => { e.preventDefault(); props.setCloseConfirmVisible(true); }}
                            >
                                Confirm Close
                            </Button>
                        </div>
                        <Modal
                            onDismiss={() => props.setCloseConfirmVisible(false)}
                            visible={props.closeConfirmVisible}
                            closeAriaLabel="Close modal"
                            footer={
                                <Box float="right">
                                    <SpaceBetween direction="horizontal" size="xs">
                                        <Button variant="link" onClick={() => props.setCloseConfirmVisible(false)}>Cancel</Button>
                                        <Button variant="primary" onClick={() => props.closeIssues()}>Confirm</Button>
                                    </SpaceBetween>
                                </Box>
                            }
                            header="Close Selected Issues"
                        >
                            <Box>Are you sure you want to close {props.selectedIssues.length} selected issue{props.selectedIssues.length > 1 ? 's' : ''}?</Box>
                        </Modal>
                    </SpaceBetween>
                )}
            </SpaceBetween>
        </Container>
    );
}


function FileDetails(props) {
    if(props.selectedFile) {
        return(
            <Container
                header={
                    <Header
                        variant="h4"
                    >
                    Attachment Details
                    </Header>
                }
                >
                <ColumnLayout columns={3} variant="text-grid">
                    <ValueWithLabel label="File name">{props.selectedFile.name}</ValueWithLabel>
                    <ValueWithLabel label="Size">{formatBytes(props.selectedFile.size)}</ValueWithLabel>
                </ColumnLayout>
            </Container>
        );
    }
    return '';
}

function SearchButton(props) {
    if(props.loading) {
        return <Spinner />; 
    }
    return(<Button onClick={(e) => {e.preventDefault(); props.searchJiras();}} variant="primary">Search</Button>);
}

function UpdateButton(props) {
    if(props.loading) {
        return <Spinner />; 
    }
    if(props.disabled || !props.selectedJiras || props.selectedJiras.length === 0) {
        return(<Button disabled variant="primary">Update Jiras</Button>);
    }
    return(<Button onClick={(e) => {e.preventDefault(); props.updateJiras();}} variant="primary">Update Jiras</Button>);
}

export default function JiraSearchPage() {
    const dispatch = useDispatch();
    const userAlias = useSelector((state) => state.currentViews.loggedInUser);
    const fileInputRef = useRef();
    const selectFile = () => {
        fileInputRef.current.click();
    }
   
    const [loading, setLoading] = React.useState(false);  
    const [cveIds, setCveIds] = React.useState([{ key: '' }]);
    const isStagingSite = getCurEnv() !== 'prod';
    const [environment, setEnvironment] = React.useState(
        isStagingSite
            ? { label: "Staging", value: "staging" }
            : { label: "Production", value: "production" }
    );
    const [asbDataSelection, setASBDataSelection] = React.useState({});
    const [matchedJiras, setMatchedJiras] = React.useState(null);
    const [matchedShepherds, setMatchedShepherds] = React.useState(null);
    const [selectedIssues, setSelectedIssues] = React.useState([]);
    const [jiraChangelog, setJiraChangelog] =  React.useState(null);
    const [updateComment, setUpdateComment] = React.useState(null);
    const [updateLabels, setUpdateLabels] = React.useState([]);
    const [updateAttachment, setUpdateAttachment] = React.useState(null);
    const[jiraChangelogModalVisible, setJiraChangelogModalVisible] = React.useState(false);
    const [activeTabId, setActiveTabId] = React.useState("jira");
    const [closeConfirmVisible, setCloseConfirmVisible] = React.useState(false);
    const [activeAction, setActiveAction] = React.useState(null);
    const [cveFilter, setCveFilter] = React.useState({ label: "All CVEs", value: "all" });
    const [showCveErrors, setShowCveErrors] = React.useState(false);
    const DEFAULT_CLOSE_COMMENT = "Manually closed by Digital Security Team. There is no further action required on this ticket. In the event that the ticket did not fully close due to an error, you may close the issue.";
    const [closeComment, setCloseComment] = React.useState(DEFAULT_CLOSE_COMMENT);

    const hasShepherdSelected = selectedIssues.some(item => item.filing_system && item.filing_system.toLowerCase() === 'shepherd');
    const selectedJiras = selectedIssues.filter(item => !item.filing_system || item.filing_system.toLowerCase() !== 'shepherd');
    const selectedShepherds = selectedIssues.filter(item => item.filing_system && item.filing_system.toLowerCase() === 'shepherd');

    // Build CVE filter options from search results
    const cveFilterOptions = useMemo(() => {
        const cves = new Set();
        if(matchedJiras) matchedJiras.forEach(j => { if(j.cve) cves.add(j.cve); });
        if(matchedShepherds) matchedShepherds.forEach(s => { if(s.cve) cves.add(s.cve); });
        return [
            { label: "All CVEs", value: "all" },
            ...Array.from(cves).sort().map(cve => ({ label: cve, value: cve }))
        ];
    }, [matchedJiras, matchedShepherds]);

    // Apply CVE filter to displayed items
    const filterByCve = (items) => {
        if(!items || cveFilter.value === 'all') return items;
        return items.filter(item => item.cve === cveFilter.value);
    };
    const displayedJiras = filterByCve(matchedJiras);
    const displayedShepherds = filterByCve(matchedShepherds);

    const [jiraSortingColumn, setJiraSortingColumn] = React.useState(null);
    const [jiraSortingDescending, setJiraSortingDescending] = React.useState(false);
    const [shepherdSortingColumn, setShepherdSortingColumn] = React.useState(null);
    const [shepherdSortingDescending, setShepherdSortingDescending] = React.useState(false);

    const sortItems = (items, sortingColumn, descending) => {
        if(!items || !sortingColumn) return items;
        const field = sortingColumn.sortingField;
        return [...items].sort((a, b) => {
            const valA = (a[field] || '').toString().toLowerCase();
            const valB = (b[field] || '').toString().toLowerCase();
            const result = valA.localeCompare(valB);
            return descending ? -result : result;
        });
    };

    const sortedJiras = sortItems(displayedJiras, jiraSortingColumn, jiraSortingDescending);
    const sortedShepherds = sortItems(displayedShepherds, shepherdSortingColumn, shepherdSortingDescending);

    React.useEffect(() => {
        dispatch(setPageTitle('ASB Issue Search'));
    }, [])

    const labelDefinition = useMemo(
        () => [
          {
            label: 'Add Labels',
            control: ({ key = '' }, itemIndex) => (
              <ItemControl prop="key" value={key} index={itemIndex} placeholder="Enter label text" setItems={setUpdateLabels} />
            )
          }
        ],
        []
    );

    const onAddLabelButtonClick = useCallback(() => {
        setUpdateLabels(items => [...items, {}]);
    }, []);

    const onRemoveLabelButtonClick = useCallback(({ detail: { itemIndex } }) => {
        setUpdateLabels(items => {
            const newItems = items.slice();
            newItems.splice(itemIndex, 1);
            return newItems;
        });
    }, []);

    const cveDefinition = useMemo(
        () => [
          {
            label: 'CVE IDs',
            control: ({ key = '' }, itemIndex) => (
              <ItemControl prop="key" value={key} index={itemIndex} placeholder="Enter CVE ID (e.g. CVE-2024-1234)" setItems={(fn) => { setShowCveErrors(false); setCveIds(fn); }} validate={(v) => /^CVE-\d{4}-\d{4,}$/i.test(v)} showErrors={showCveErrors} />
            )
          }
        ],
        [showCveErrors]
    );

    const onAddCveButtonClick = useCallback(() => {
        setCveIds(items => [...items, {}]);
    }, []);

    const onRemoveCveButtonClick = useCallback(({ detail: { itemIndex } }) => {
        setCveIds(items => {
            const newItems = items.slice();
            newItems.splice(itemIndex, 1);
            return newItems;
        });
    }, []);

    //Search for issues across all specified CVEs
    const searchJiras = async() => {
        setLoading(true);
        setMatchedJiras(null);
        setMatchedShepherds(null);
        setSelectedIssues([]);
        setJiraChangelog(null);
        dispatch(setStatusBarMessage(null));
        setUpdateComment(null);
        setUpdateLabels([]);
        setUpdateAttachment(null);
        setActiveAction(null);
        setCloseComment(DEFAULT_CLOSE_COMMENT);
        setCveFilter({ label: "All CVEs", value: "all" });

        const validCves = cveIds.map(item => (item.key || '').trim().toUpperCase()).filter(cve => cve.length > 0);

        if(validCves.length === 0) {
            dispatch(setStatusBarMessage({type: "warning", message: "Please add at least one CVE ID."}));
            setLoading(false);
            return;
        }

        const cvePattern = /^CVE-\d{4}-\d{4,}$/i;
        const invalidCves = validCves.filter(cve => !cvePattern.test(cve));
        if(invalidCves.length > 0) {
            setShowCveErrors(true);
            dispatch(setStatusBarMessage({type: "error", message: "Invalid CVE format: " + invalidCves.join(', ') + ". Expected format: CVE-YYYY-NNNNN"}));
            setLoading(false);
            return;
        }

        if(validCves.length > 0) {
            let allJiras = [];
            let allShepherds = [];
            let allChangelogs = {};

            // Build optional ASB month filter once (shared across all CVE searches)
            let asbMonth = null;
            if(asbDataSelection && asbDataSelection.label) {
                let month_name = asbDataSelection.label.split(' ')[0].trim();
                let year = asbDataSelection.label.split(' ')[1].trim();
                let month_num = -1;
                for(let i = 0; i < MONTHS.length; i++) {
                    if(MONTHS[i] === month_name) {
                        month_num = i;
                        break;
                    }
                }
                month_num = month_num < 10 ? '0' + month_num.toString() : month_num.toString();
                asbMonth = 'Android_Security_Bulletin_' + month_name + '_' + year + '-' + month_num;
            }

            // Search for each CVE in parallel
            const searchPromises = validCves.map(async (cve) => {
                let searchObj = { "cve": cve };
                if(asbMonth) {
                    searchObj['asb_month'] = asbMonth;
                }

                const options = {
                    headers: {
                        'Authorization': `Bearer ${getJwtToken()}`,
                        'Content-Type': 'application/json'
                    },
                    params: {
                        "search": JSON.stringify(searchObj),
                        "environment": environment['value']
                    }
                };

                let jiraResults = [];
                let shepherdResults = [];
                let changelog = {};
                let errors = [];

                try {
                    // Get Jira search results from Lambda endpoint
                    let search_response = await axios.get(`${getApiURL()}/jira/update`, options);
                    if(search_response.status === 200) {
                        if(search_response['data'] && search_response['data']['matched_jiras']) {
                            jiraResults = search_response['data']['matched_jiras'].map(jira => ({ ...jira, cve }));
                            let ids = jiraResults.map(r => r.id);

                            // Get changelog for matched Jiras
                            if(ids.length > 0) {
                                try {
                                    let record_response = await axios.get(`${getApiURL()}/jira?ids=` + ids.join(','), {
                                        headers: {
                                            'Authorization': `Bearer ${getJwtToken()}`,
                                            'Content-Type': 'application/json',
                                        }
                                    });
                                    if(record_response['data']) {
                                        changelog = record_response['data'];
                                    }
                                } catch (e) {
                                    console.log("Changelog fetch error for " + cve + ":", e);
                                }
                            }
                        }
                    }
                } catch (e) {
                    console.log("Jira search error for " + cve + ":", e);
                    const status = e.response ? e.response.status : 'unknown';
                    errors.push({ type: 'search', cve, status });
                }

                // Search for Shepherd issues
                try {
                    let shepherd_response = await axios.get(`${getApiURL()}/jira/search`, {
                        headers: {
                            'Authorization': `Bearer ${getJwtToken()}`,
                            'Content-Type': 'application/json'
                        },
                        params: { cve, environment: environment['value'] }
                    });
                    let all_issues = shepherd_response['data'] || [];
                    shepherdResults = all_issues
                        .filter(issue => issue.filing_system && issue.filing_system.toLowerCase() === 'shepherd' && issue.cve && issue.cve === cve)
                        .filter(issue => !asbDataSelection || !asbDataSelection.label || (issue.asb && issue.asb === asbDataSelection.label))
                        .map(issue => ({ ...issue, cve }));
                } catch (shepherd_error) {
                    console.log("Shepherd search error for " + cve + ":", shepherd_error);
                }

                return { jiraResults, shepherdResults, changelog, errors };
            });

            try {
                const results = await Promise.all(searchPromises);
                let allErrors = [];
                for(const result of results) {
                    allJiras = allJiras.concat(result.jiraResults);
                    allShepherds = allShepherds.concat(result.shepherdResults);
                    Object.assign(allChangelogs, result.changelog);
                    allErrors = allErrors.concat(result.errors);
                }
                setMatchedJiras(allJiras);
                setMatchedShepherds(allShepherds);
                setJiraChangelog(allChangelogs);
                if(allErrors.length > 0) {
                    const message = "Search failed for " + allErrors.map(e => e.cve + " (Response " + e.status + ")").join(', ');
                    dispatch(setStatusBarMessage({type: "error", message}));
                }
            } catch (e) {
                dispatch(setStatusBarMessage({type: "error", message: "Failed to search for issues: " + e}));
                console.log(e);
            }
        }

        setLoading(false);
    }

    const updateJiras = async() => {
        setLoading(true);
        dispatch(setStatusBarMessage(null));

        if(!selectedJiras || selectedJiras.length < 1) {
            dispatch(setStatusBarMessage({type: "warning", message: "No Jiras selected to update"}));
            setLoading(false);
            return;
        }

        if((!updateComment || updateComment.length < 1) && (!updateAttachment) && (updateLabels.length < 1)) {
            dispatch(setStatusBarMessage({type: "warning", message: "No updates specified."}));
        }
        else if(updateAttachment && (!updateComment || updateComment.length < 1)) {
            dispatch(setStatusBarMessage({type: "error", message: "Attachment specified but no comment provided."}));
        }
        else {
            if(updateAttachment) {
                try {
                    //Get presigned URL
                    const presigned_url_options = {
                        headers: {
                            'Authorization': `Bearer ${getJwtToken()}`,
                            'Content-Type': 'application/json'
                        },
                    };
                    const presigned_url_data = {file_name: updateAttachment.name};
                    let presigned_url_response = await axios.post(`${getApiURL()}/jira/attachment`, presigned_url_data, presigned_url_options);
                    
                    //Upload data to S3
                    let payload = new FormData();
                    Object.entries(presigned_url_response.data.fields).forEach(([ key, val ]) => {
                        payload.append(key, val);
                    });
                    payload.append("file", updateAttachment); 
        
                    let response = await axios.post(presigned_url_response.data.url, payload, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }    
                    });

                    if(response.status < 200 || response.status >= 300 ) {
                        throw 'Attachment upload failed.';
                    }
                } catch (e) {
                    dispatch(setStatusBarMessage({type: "error", message: "Failed to update Jiras: " + e}));
                    console.log(e);
                    setLoading(false);
                    return;
                }
            }
            
            let params = {
                "jira_ids": [],
                "environment": environment['value']
            }
            for(const jira of selectedJiras) { //Add Jira IDs to request body
                params['jira_ids'].push(jira['id']);
            }
            if(updateComment) { //Add comment text to request body
                params['comment'] = updateComment;
            }
            if(updateAttachment) { //Add attachment name to request body
                params['attachment'] = updateAttachment.name;
            }
            if(updateLabels.length > 0) { //Add new labels to request body
                params['labels_append'] = [];
                for(const label of updateLabels) {
                    params['labels_append'].push(label.key);
                }
            }

            const options = {
                headers: {
                    'Authorization': `Bearer ${getJwtToken()}`,
                    'Content-Type': 'application/json'
                },
                params: params
            }
        
            try {
                let response = await axios.get(`${getApiURL()}/jira/update`, options);
                console.log(response.status);
    
                if(response['data']) {
                    dispatch(setStatusBarMessage({type: response.status === 200 ? 'success' : 'error', message: response['data']}));
                }
                
            } catch (e) {
                dispatch(setStatusBarMessage({type: "error", message: "Failed to update Jiras: " + e}));
                console.log(e);
            }
        }

        setLoading(false);
    }

    const closeIssues = async() => {
        setLoading(true);
        setCloseConfirmVisible(false);
        dispatch(setStatusBarMessage(null));

        if(!selectedIssues || selectedIssues.length < 1) {
            dispatch(setStatusBarMessage({type: "warning", message: "No issues selected to close"}));
            setLoading(false);
            return;
        }

        const validCves = cveIds.map(item => (item.key || '').trim().toUpperCase()).filter(cve => cve.length > 0);
        const jobId = uuidv4();
        const issueIds = selectedIssues.map(issue => issue['id']).join(',');
        const params = `--run-id ${jobId} --mode close-tickets --environment ${environment['value']} --close-issues ${issueIds} --requester-alias ${userAlias}${closeComment ? ` --close-comment ${btoa(closeComment)}` : ''}`;

        const options = {
            headers: {
                'Authorization': `Bearer ${getJwtToken()}`,
                'Content-Type': 'application/json'
            }
        };

        const data = {
            job_id: jobId,
            job_description: `Close ${selectedIssues.length} issue(s) for ${validCves.join(', ')}`,
            params: params
        };

        try {
            let response = await axios.post(`${getApiURL()}/job`, data, options);
            if(response.status === 200) {
                dispatch(setStatusBarMessage({type: 'success', message: `Closure job created. Redirecting to job ${response['data']}...`}));
                setSelectedIssues([]);
                setTimeout(() => {
                    window.location = '/jobs/' + response['data'];
                }, 1500);
            } else {
                dispatch(setStatusBarMessage({type: 'error', message: response['data'] || 'Failed to create closure job'}));
            }
        } catch (e) {
            dispatch(setStatusBarMessage({type: "error", message: "Failed to create closure job: " + e}));
            console.log(e);
        }

        setLoading(false);
    }

    return (
        <SpaceBetween size='l'>
            <Container
                header={
                    <Header
                        variant="h2"
                    >
                        Search Parameters
                    </Header>
                }
            >
                <SpaceBetween size="s">
                    <AttributeEditor
                        items={cveIds}
                        definition={cveDefinition}
                        onAddButtonClick={onAddCveButtonClick}
                        onRemoveButtonClick={onRemoveCveButtonClick}
                        addButtonText="Add CVE"
                        removeButtonText="Remove"
                        empty="No CVEs added. Click 'Add CVE' to begin."
                    />
                    <FormField
                        label="Environment"
                    >
                        <Select
                            selectedOption={environment}
                            onChange={({ detail }) =>
                                setEnvironment(detail.selectedOption)
                            }
                            options={[
                                { label: "Production", value: "production" },
                                { label: "Staging", value: "staging" }
                            ]}
                            selectedAriaLabel="Selected"
                        />
                    </FormField>
                    <ASBMonthSelector selectedOption={asbDataSelection} setSelectedOption={setASBDataSelection} optional={true}/>
                    <div align="right">
                        <SearchButton loading={loading} searchJiras={searchJiras} />
                    </div>
                </SpaceBetween>
            </Container>

            {(matchedJiras || matchedShepherds) && (
                <SpaceBetween size="s">
                    <SpaceBetween direction="horizontal" size="m">
                        <FormField label="Filter by CVE">
                            <Select
                                selectedOption={cveFilter}
                                onChange={({ detail }) => setCveFilter(detail.selectedOption)}
                                options={cveFilterOptions}
                                selectedAriaLabel="Selected"
                            />
                        </FormField>
                    </SpaceBetween>
                    {selectedIssues.length > 0 && (
                        <Alert type="info" dismissible={false}>
                            <SpaceBetween direction="horizontal" size="xs">
                                <span>{selectedIssues.length} issue{selectedIssues.length !== 1 ? 's' : ''} selected:</span>
                                {Object.entries(
                                    selectedIssues.reduce((acc, item) => {
                                        const cve = item.cve || 'Unknown';
                                        acc[cve] = (acc[cve] || 0) + 1;
                                        return acc;
                                    }, {})
                                ).sort(([a], [b]) => a.localeCompare(b)).map(([cve, count]) => (
                                    <Badge key={cve} color="blue">{cve}: {count}</Badge>
                                ))}
                            </SpaceBetween>
                        </Alert>
                    )}
                </SpaceBetween>
            )}

            <Tabs
                activeTabId={activeTabId}
                onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
                tabs={[
                    {
                        id: "jira",
                        label: `Jira Issues${sortedJiras ? ` (${sortedJiras.length})` : ''}`,
                        content: (
                            <SearchResultsTable 
                                    jiras={sortedJiras} 
                                    selectedJiras={selectedJiras}
                                    setSelectedIssues={setSelectedIssues}
                                    environment={environment}
                                    jiraChangelog={jiraChangelog}
                                    jiraChangelogModalVisible={jiraChangelogModalVisible}
                                    setJiraChangelogModalVisible={setJiraChangelogModalVisible}
                                    sortingColumn={jiraSortingColumn}
                                    sortingDescending={jiraSortingDescending}
                                    onSortingChange={({ detail }) => {
                                        setJiraSortingColumn(detail.sortingColumn);
                                        setJiraSortingDescending(detail.isDescending);
                                    }}
                                />
                        )
                    },
                    {
                        id: "shepherd",
                        label: `Shepherd Issues${sortedShepherds ? ` (${sortedShepherds.length})` : ''}`,
                        content: (
                            <ShepherdResultsTable 
                                shepherds={sortedShepherds}
                                selectedShepherds={selectedShepherds}
                                setSelectedIssues={setSelectedIssues}
                                environment={environment}
                                sortingColumn={shepherdSortingColumn}
                                sortingDescending={shepherdSortingDescending}
                                onSortingChange={({ detail }) => {
                                    setShepherdSortingColumn(detail.sortingColumn);
                                    setShepherdSortingDescending(detail.isDescending);
                                }}
                            />
                        )
                    }
                ]}
            />
            <UpdateJiraForm 
                loading={loading}
                selectedIssues={selectedIssues}
                selectedJiras={selectedJiras}
                selectedShepherds={selectedShepherds}
                hasShepherdSelected={hasShepherdSelected}
                activeAction={activeAction}
                setActiveAction={setActiveAction}
                updateComment={updateComment} 
                setUpdateComment={setUpdateComment}
                closeComment={closeComment}
                setCloseComment={setCloseComment}
                jiraLabels={updateLabels}
                setJiraLabels={setUpdateLabels}
                labelDefinition={labelDefinition}
                onAddLabelButtonClick={onAddLabelButtonClick}
                onRemoveLabelButtonClick={onRemoveLabelButtonClick}
                fileInputRef={fileInputRef}
                selectedFile={updateAttachment}
                setSelectedFile={setUpdateAttachment}
                selectFile={selectFile}
                updateJiras={updateJiras}
                closeIssues={closeIssues}
                closeConfirmVisible={closeConfirmVisible}
                setCloseConfirmVisible={setCloseConfirmVisible}
            />
        </SpaceBetween>
    );
}